/**
 * TasksPRStatus - Tasks and PR status chart component
 *
 * Displays monthly trend chart for tasks and PRs using Recharts
 */

import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { HistoryDataPoint } from '@/lib/dashboard-api.ts';

interface ChartData {
  label: string;
  dateLabel: string;
  accepted: number;
  rejected: number;
  open: number;
}

interface TooltipEntry {
  color?: string;
  dataKey?: string | number;
  name?: string;
  payload?: ChartData;
  value?: number;
}

interface TasksPRStatusProps {
  monthlyHistory: HistoryDataPoint[];
}

interface StatusTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
}

const CHART_COLORS = {
  accepted: '#FFE8A3',
  rejected: '#E8B931',
  open: '#E5A000',
};
const TOP_BAR_RADIUS: [number, number, number, number] = [6, 6, 0, 0];
const X_AXIS_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});
type StatusKey = 'accepted' | 'rejected' | 'open';

function getTopBarRadius(
  dataPoint: ChartData,
  key: StatusKey
): [number, number, number, number] | 0 {

  const stackOrder: StatusKey[] = ['open', 'rejected', 'accepted'];

  const topSegment = stackOrder.find(status => dataPoint[status] > 0);

  return key === topSegment ? TOP_BAR_RADIUS : 0;
}

function renderStackBarShape(key: StatusKey) {
  // eslint-disable-next-line react/display-name
  return (props: object) => {
    const barProps = props as { payload: ChartData };
    return <Rectangle {...(props as object)} radius={getTopBarRadius(barProps.payload, key)} />;
  };
}

function StatusTooltip({ active, payload }: StatusTooltipProps) {
  if (!active || !payload || payload.length === 0 || !payload[0].payload) {
    return null;
  }

  const data = payload[0].payload;
  const total = data.accepted + data.rejected + data.open;
  const acceptanceRate = total > 0 ? Math.round((data.accepted / total) * 100) : 0;

  return (
    <div className="rounded-lg bg-white dark:bg-gray-800 dark:text-white p-3 font-caption border border-gray-300 dark:border-gray-600 ring-1 ring-white/10">
      <div className="mb-2 font-semibold">{data.dateLabel}</div>
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CHART_COLORS.accepted }} />
          <span className='text-info-DEFAULT'>Accepted</span>
          <span>{data.accepted}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CHART_COLORS.rejected }} />
          <span className='text-info-DEFAULT'>Rejected</span>
          <span>{data.rejected}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CHART_COLORS.open }} />
          <span className='text-info-DEFAULT'>Open</span>
          <span>{data.open}</span>
        </div>
      </div>
      <div className="my-2 h-px bg-gray-400" />
      <div className="text-info-DEFAULT">
        Total: <span className='text-black dark:text-white'>{total}</span> | Acceptance: <span className='text-black dark:text-white'>{acceptanceRate}%</span>
      </div>
    </div>
  );
}

function AcceptanceStats({ data }: { data: ChartData[] }) {
  const avgAcceptance = useMemo(() => {
    if (data.length === 0) return 0;
    return Math.round(
      data.reduce((sum, d) => {
        const total = d.accepted + d.rejected + d.open;
        return sum + (total > 0 ? (d.accepted / total) * 100 : 0);
      }, 0) / data.length
    );
  }, [data]);

  const bestDay = useMemo(() => {
    if (data.length === 0) return 0;
    return Math.round(
      Math.max(
        ...data.map((d) => {
          const total = d.accepted + d.rejected + d.open;
          return total > 0 ? (d.accepted / total) * 100 : 0;
        })
      )
    );
  }, [data]);

  const latestAcceptance = useMemo(() => {
    if (data.length === 0) return 0;
    const latest = data[data.length - 1];
    const total = latest.accepted + latest.rejected + latest.open;
    return total > 0 ? Math.round((latest.accepted / total) * 100) : 0;
  }, [data]);

  return (
    <div className="mt-6 border-t border-gray-100 pt-6 dark:border-gray-800">
      <div className="grid grid-cols-3 gap-4 text-center">
        <p>
          <span className="mr-1.5 font-caption text-gray-500 dark:text-gray-400">Avg Acceptance</span>
          <span className="font-body-sm  text-gray-900 dark:text-white">{avgAcceptance}%</span>
        </p>
        <p>
          <span className="mr-1.5 font-caption text-gray-500 dark:text-gray-400">Best Day</span>
          <span className="font-body-sm text-gray-900 dark:text-white">{bestDay}%</span>
        </p>
        <p className=''>
          <span className="mr-1.5 font-caption text-gray-500 dark:text-gray-400">Latest</span>
          <span className="font-body-sm text-gray-900 dark:text-white">{latestAcceptance}%</span>
        </p>
      </div>
    </div>
  );
}

export function TasksPRStatus({ monthlyHistory }: TasksPRStatusProps) {
  const monthlyData = useMemo<ChartData[]>(() => {
    return monthlyHistory.map((point) => {
      const date = new Date(point.date);
      const formattedDate = X_AXIS_DATE_FORMATTER.format(date).replace(',', '');
      return {
        label: formattedDate,
        dateLabel: formattedDate,
        accepted: point.prsMerged,
        rejected: point.prsClosed,
        open:  point.prsOpen,
      };
    });
  }, [monthlyHistory]);

  return (
    <div className="rounded-xl border border-gray-200/60 bg-white p-6 shadow-sm dark:border-gray-800/50 dark:bg-gray-900">
      <div>
        {/* Monthly Trend */}
        <div>
          <div className="mb-6 flex items-center justify-between gap-3">
            <h4 className="font-body-base text-gray-700 dark:text-gray-300">Monthly Trend (Last 30 Days)</h4>
            <div className="flex font-caption items-center gap-4 text-xs text-gray-600 dark:text-gray-300">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: CHART_COLORS.accepted }} />
                Accepted
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: CHART_COLORS.rejected }} />
                Rejected
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: CHART_COLORS.open }} />
                Open
              </span>
            </div>
          </div>
          <div className="h-60 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(107,114,128,0.2)" vertical={false} />
                <XAxis
                  dataKey="label"
                  interval="preserveStartEnd"
                  minTickGap={18}
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={28} />
                <Tooltip content={<StatusTooltip />} cursor={{ fill: 'rgba(59,130,246,0.06)' }} />
                <Bar
                  dataKey="accepted"
                  stackId="status"
                  name="Accepted"
                  fill={CHART_COLORS.accepted}
                  shape={renderStackBarShape('accepted')}
                />
                <Bar
                  dataKey="rejected"
                  stackId="status"
                  name="Rejected"
                  fill={CHART_COLORS.rejected}
                  shape={renderStackBarShape('rejected')}
                />
                <Bar
                  dataKey="open"
                  stackId="status"
                  name="Open"
                  fill={CHART_COLORS.open}
                  shape={renderStackBarShape('open')}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <AcceptanceStats data={monthlyData} />
    </div>
  );
}
